import * as React from "react";
import { useDispatch } from "react-redux";
import { Accordion, Form, Icon, Header, Container, Grid } from "semantic-ui-react";

import { ThunkDispatch } from "redux-thunk";

import { QuestionnaireSectionInjectedProps, QuestionnaireSectionOwnProps } from "../QuestionnaireSectionProps";
import { renderQuestions, isSectionVisible, registerQuestionsForSection } from "../utils";
import {
    nextSection, removeQuestion, toggleSection, updateQuestion, validateQuestion
} from "../actions";
import { AnsweredQuestion, QuestionMap } from "../model";

import { useQuestionnaireContainerForSection, useSectionWithQuestionnaireContainer } from "./QuestionnaireContainerContext";
import { QuestionnaireSectionProvider } from "./QuestionnaireSectionContext";

interface AccordionQuestionnaireSectionOwnProps {
    hideNextButton?: boolean;
    additionalIsValid?: boolean;
}

type AccordionQuestionnaireSectionProps =
    Omit<QuestionnaireSectionOwnProps, keyof QuestionnaireSectionInjectedProps> &
    AccordionQuestionnaireSectionOwnProps;

export const AccordionQuestionnaireSection =({ ...props }: AccordionQuestionnaireSectionProps) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    useSectionWithQuestionnaireContainer(props);
    const { section, ...questionnaireContext } = useQuestionnaireContainerForSection(props.id);
    const onClick = React.useCallback(() => {
        dispatch(toggleSection({ sectionId: props.id, questionnaireKey: questionnaireContext.questionnaireKey }));
    }, [dispatch, props.id, questionnaireContext.questionnaireKey]);
    const questions: QuestionMap = React.useMemo(() => section.questions || {}, [section]);
    React.useEffect(() => {
        // hopefully we can remove this horrible registration code soon and only use React contexts
        registerQuestionsForSection({
            questions,
            id: props.id,
            children: props.children,
            questionnaireKey: questionnaireContext.questionnaireKey,
            removeQuestion: (payload: { sectionId: string; id: string; questionnaireKey: string }) => dispatch(removeQuestion(payload)),
            updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => dispatch(updateQuestion({ ...payload, questionnaireKey: questionnaireContext.questionnaireKey })), // eslint-disable-line max-len
        });
    }, [dispatch, props.children, props.id, questionnaireContext.questionnaireKey, questions]);

    const onNextClick = React.useCallback(() => {
        dispatch(nextSection({ sectionId: props.id, questionnaireKey: questionnaireContext.questionnaireKey }));
    }, [dispatch, props.id, questionnaireContext.questionnaireKey]);

    if (questionnaireContext === null || isSectionVisible({
        questions,
        neverHide: props.neverHide,
    }) !== true) {
        return null;
    }

    const active = props.active || section.active;
    return (
        <QuestionnaireSectionProvider id={props.id}>
            <Accordion.Title active={active} onClick={onClick}>
                <Grid columns="2">
                    <Grid.Column width="15" floated="left">
                        <Header as="h4">{props.title}</Header>
                    </Grid.Column>
                    {section.isComplete && !questionnaireContext.readonly &&
                        <Grid.Column width="1" textAlign="right">
                            {section.isValid && (props.additionalIsValid === undefined || props.additionalIsValid) ?
                                <Icon name="check circle" color="green" /> :
                                <Icon name="times circle" color="red" />}
                        </Grid.Column>
                    }
                </Grid>
            </Accordion.Title>
            <Accordion.Content className="content-section" active={active}>
                {renderQuestions({
                    id: props.id,
                    children: props.children,
                    questions,
                    questionnaireKey: questionnaireContext.questionnaireKey,
                    isComplete: section.isComplete,
                    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => dispatch(updateQuestion({ ...payload, questionnaireKey: questionnaireContext.questionnaireKey })), // eslint-disable-line max-len
                    validateQuestion: (payload: { sectionId: string; id: string; validatedQuestion: Promise<AnsweredQuestion>; questionnaireKey: string }) => dispatch(validateQuestion(payload)) // eslint-disable-line max-len
                })}
                {!questionnaireContext.readonly && !props.hideNextButton &&
                    <Grid container>
                        <Grid.Column>
                            <Form.Field>
                                <Container fluid>
                                    <Form.Button
                                        type="button"
                                        onClick={onNextClick}
                                    >
                                        Next
                                    </Form.Button>
                                </Container>
                            </Form.Field>
                        </Grid.Column>
                    </Grid>
                }
            </Accordion.Content>
        </QuestionnaireSectionProvider>
    );
};
